import {
  FaroChip,
  FaroChipProps,
  grey,
  NO_TRANSLATE_CLASS,
} from "@faro-lotv/flat-ui";
import type { JSX } from "react";
import {
  AI_GRADIENT,
  AI_GRADIENT_BASE_COLOR,
  AI_GRADIENT_SMALL,
} from "./ai-gradients";

type FaroAiChipProps = Pick<
  FaroChipProps,
  "label" | "size" | "textTransform" | "onClick" | "disabled" | "tooltip"
>;

/** @returns a specialized chip component for AI-generated tags with gradient styling */
export function FaroAiChip({
  label,
  size = "medium",
  textTransform = "capitalize",
  onClick,
  disabled,
  tooltip,
}: FaroAiChipProps): JSX.Element {
  return (
    <FaroChip
      className={NO_TRANSLATE_CLASS}
      label={label}
      textTransform={textTransform}
      size={size}
      onClick={onClick}
      disabled={disabled}
      tooltip={tooltip}
      color={grey[0]}
      backgroundColor={AI_GRADIENT_BASE_COLOR}
      sx={{
        ...AI_GRADIENT,
        transition: "filter 150ms",
        // The gradient is opaque, so MUI's hover background color would never show through.
        "&.MuiChip-clickable:hover": { filter: "brightness(0.95)" },
      }}
    />
  );
}

/**
 * The design's `extraSmall` chip size (18px, 10px SemiBold), not available in flat-ui yet: it comes
 * with the `Chip` of the dark-mode release
 */
export const EXTRA_SMALL_CHIP_SX = {
  height: 18,
  "& .MuiChip-label": {
    px: "11px",
    fontSize: "10px",
    lineHeight: "16px",
    fontWeight: 600,
  },
};

/**
 * @returns a chip outlined with the AI gradient, with a light label, for dark surfaces such as previews.
 * Stand-in for flat-ui's `Chip color="ai" variant="outlined"` of the dark-mode release, whose ring it copies
 */
export function FaroAiOutlinedChip({
  label,
  tooltip,
}: Pick<FaroAiChipProps, "label" | "tooltip">): JSX.Element {
  return (
    <FaroChip
      label={label}
      size="small"
      textTransform="none"
      tooltip={tooltip}
      color={grey[100]}
      backgroundColor="transparent"
      sx={{
        ...EXTRA_SMALL_CHIP_SX,
        position: "relative",
        border: "1px solid transparent",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: "-1px",
          padding: "1px",
          borderRadius: "inherit",
          ...AI_GRADIENT_SMALL,
          pointerEvents: "none",
          // Keep only the 1px ring between the padding box and the border box
          mask: "linear-gradient(#000 0 0) content-box exclude, linear-gradient(#000 0 0)",
          WebkitMask:
            "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
          WebkitMaskComposite: "xor",
        },
      }}
    />
  );
}
